import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";

const STATUS_CONFIG = {
  submitted: {
    label: "Submitted",
    desc: "We've received your application.",
    color: "bg-white/10 text-white border-white/20",
    dot: "bg-white",
  },
  reviewing: {
    label: "Under Review",
    desc: "Our team is currently reviewing it.",
    color: "bg-[#ffd978]/15 text-[#ffd978] border-[#ffd978]/30",
    dot: "bg-[#ffd978]",
  },
  accepted: {
    label: "Accepted",
    desc: "Congratulations — you've been accepted!",
    color: "bg-green-500/15 text-green-400 border-green-500/30",
    dot: "bg-green-400",
  },
  rejected: {
    label: "Not Selected",
    desc: "Thank you for applying.",
    color: "bg-red-500/15 text-red-400 border-red-500/30",
    dot: "bg-red-400",
  },
};

export default function MyApplication() {
  const { session } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loggingIn, setLoggingIn] = useState(false);

  useEffect(() => {
    if (session) loadApplications();
    else setLoading(false);
  }, [session]);

  async function loadApplications() {
    setLoading(true);

    const discordIdentity = session?.user?.identities?.find(i => i.provider === "discord");
    const discordId = discordIdentity?.id || session?.user?.user_metadata?.provider_id;

    if (!discordId) {
      setLoading(false);
      return;
    }

    const { data } = await supabase
      .from("career_applications")
      .select("id, status, created_at, careers(title, department)")
      .eq("discord_id", discordId)
      .order("created_at", { ascending: false });

    setApplications(data || []);
    setLoading(false);
  }

  async function handleDiscordLogin() {
    setLoggingIn(true);
    await supabase.auth.signInWithOAuth({
      provider: "discord",
      options: { redirectTo: window.location.href },
    });
  }

  const discordUser = session?.user?.user_metadata;

  return (
    <main className="min-h-screen bg-[#050505] text-white">
      <Header />

      <section className="px-6 pt-36 pb-20 md:px-[7%]">
        <p className="font-black uppercase text-[#ffb56f]">Applicant Portal</p>
        <h1 className="outfall-heading text-[clamp(58px,9vw,120px)] leading-none text-[#ffd978] drop-shadow-[0_0_18px_#ff8f6b]">
          My Applications
        </h1>

        {!session ? (
          <div className="mt-10 max-w-lg rounded-[24px] border-4 border-[#ffd978] bg-black/60 p-8 shadow-[0_0_35px_rgba(255,143,107,0.35)]">
            <p className="mb-6 font-bold text-white/60">
              Sign in with the same Discord account you used to apply to see your application status.
            </p>
            <button
              onClick={handleDiscordLogin}
              disabled={loggingIn}
              className="flex items-center gap-3 rounded-xl bg-[#5865F2] px-6 py-4 font-black text-white transition hover:bg-[#4752C4] disabled:opacity-50"
            >
              <svg width="24" height="18" viewBox="0 0 24 18" fill="currentColor">
                <path d="M20.317 1.492a19.825 19.825 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.294 18.294 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 1.492a.07.07 0 0 0-.032.027C.533 6.093-.32 10.555.099 14.961a.08.08 0 0 0 .031.055 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.442a.061.061 0 0 0-.031-.028z"/>
              </svg>
              {loggingIn ? "Redirecting…" : "Continue with Discord"}
            </button>
          </div>
        ) : loading ? (
          <p className="mt-10 text-xl font-black">Loading your applications...</p>
        ) : (
          <>
            <div className="mt-6 flex items-center gap-3">
              {discordUser?.avatar_url && (
                <img src={discordUser.avatar_url} alt="" className="h-8 w-8 rounded-full" />
              )}
              <p className="font-bold text-white/50">
                Signed in as <span className="font-black text-white">{discordUser?.full_name || discordUser?.name}</span>
              </p>
              <button
                onClick={() => supabase.auth.signOut()}
                className="ml-auto text-sm font-black uppercase text-white/30 hover:text-white/60"
              >
                Sign out
              </button>
            </div>

            {applications.length === 0 ? (
              <div className="mt-10 max-w-lg rounded-[24px] border-4 border-[#ffd978]/30 bg-black/40 p-8">
                <p className="font-black text-white/60">No applications found for your account.</p>
                <Link to="/careers" className="outfall-button mt-6 inline-block">
                  Browse Careers
                </Link>
              </div>
            ) : (
              <div className="mt-10 flex flex-col gap-4 max-w-3xl">
                {applications.map((app) => {
                  const cfg = STATUS_CONFIG[app.status] || STATUS_CONFIG.submitted;
                  return (
                    <div
                      key={app.id}
                      className="rounded-[20px] border-2 border-[#ffd978]/20 bg-black/50 p-6"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                          <p className="text-sm font-black uppercase text-[#ffb56f]">
                            {app.careers?.department || "Outfall"}
                          </p>
                          <h2 className="mt-1 text-2xl font-black text-white">
                            {app.careers?.title || "Unknown Role"}
                          </h2>
                          <p className="mt-1 text-sm font-bold text-white/40">
                            Submitted {new Date(app.created_at).toLocaleDateString()}
                          </p>
                        </div>

                        <div className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-black ${cfg.color}`}>
                          <span className={`h-2 w-2 rounded-full ${cfg.dot}`} />
                          {cfg.label}
                        </div>
                      </div>

                      <p className="mt-3 text-sm font-bold text-white/50">{cfg.desc}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
}
